import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, CalendarPlus } from "lucide-react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import DatePickerPopover from "./DatePickerPopover";
import { schedulePlannerUnit } from "../lib/api";

// Used from two places: Dashboard library card ("Schedule unit") + the
// post-generation popup. `activity` is the unit row (output_type='unit').
const ScheduleUnitDialog = ({ open, onOpenChange, activity, onScheduled }) => {
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  });
  const [plan, setPlan] = useState(null);
  const [warning, setWarning] = useState(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);

  useEffect(() => {
    if (!open || !activity?.id) return;
    setLoading(true);
    schedulePlannerUnit(activity.id, startDate, true)
      .then((res) => { setPlan(res.plan || []); setWarning(res.warning || null); })
      .catch((err) => {
        toast.error(err?.response?.data?.detail || "Could not preview unit schedule.");
        setPlan(null);
      })
      .finally(() => setLoading(false));
  }, [open, activity, startDate]);

  const handleConfirm = async () => {
    setCommitting(true);
    try {
      const res = await schedulePlannerUnit(activity.id, startDate, false);
      const placed = res.events?.length || 0;
      const noun = activity.output_type === "summative_task" ? "periods" : "lessons";
      toast.success(`${placed} ${noun} scheduled.`);
      onScheduled && onScheduled(res);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not schedule.");
    } finally {
      setCommitting(false);
    }
  };

  if (!activity) return null;
  // Summative tasks reuse this dialog. Wording flips from "lessons" → "periods"
  // and "Lesson N" rows become "Period N" to match the saved event names.
  const isSummative = activity.output_type === "summative_task";
  const periodWord = isSummative ? "Period" : "Lesson";
  const periodWordPlural = isSummative ? "periods" : "lessons";
  const dialogTitle = isSummative ? "Schedule summative task" : "Schedule unit";
  const n = activity.lesson_count || (activity.inputs?.num_lessons) || 0;
  const subject = activity.inputs?.subject || "—";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[calc(100vw-1.5rem)] sm:max-w-lg max-h-[calc(100vh-2rem)] overflow-y-auto"
        data-testid="schedule-unit-dialog"
      >
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription>
            {activity.activity_name} · {n} {periodWordPlural} · {subject}
          </DialogDescription>
        </DialogHeader>

        <label className="flex items-center gap-2 text-sm font-semibold text-[#4a6984]">
          Start date:
          <DatePickerPopover
            value={startDate}
            onChange={setStartDate}
            testid="schedule-unit-start-date"
            className="w-56"
          />
        </label>

        {loading && (
          <p className="text-sm text-[#4a6984]/70 flex items-center gap-1">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Finding your {subject} slots…
          </p>
        )}

        {plan && plan.length > 0 && (
          <div className="space-y-1 max-h-60 overflow-y-auto" data-testid="schedule-unit-plan">
            {plan.map((row) => (
              <div
                key={row.lesson_index}
                className={`flex items-center justify-between px-3 py-1.5 rounded text-xs ${row.overflow ? "bg-[#c45a6b]/10 text-[#c45a6b]" : "bg-[#f4f5f7] text-[#1c2d3d]"}`}
                data-testid={`schedule-unit-row-${row.lesson_index}`}
              >
                <span className="font-semibold">{periodWord} {row.lesson_index}</span>
                {row.overflow ? (
                  <span>Could not place (no slot available)</span>
                ) : (
                  <span>{row.date} · {row.period_label} ({row.day_label})</span>
                )}
              </div>
            ))}
          </div>
        )}

        {warning && (
          <p className="text-xs text-[#c45a6b] bg-[#c45a6b]/8 p-2 rounded" data-testid="schedule-unit-warning">
            {warning}
          </p>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            className="bg-[#1c2d3d] hover:bg-[#1c2d3d]/90 text-white"
            onClick={handleConfirm}
            disabled={committing || loading || !plan || plan.length === 0}
            data-testid="schedule-unit-confirm"
          >
            {committing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <CalendarPlus className="h-3.5 w-3.5 mr-1" />}
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ScheduleUnitDialog;
